import { useReducer, useRef, useState } from 'react';
import Observable from 'zen-observable';
import { registerMeetingCompleteSubscription } from '../config/appsync';
import { OnRegisterToMeetingCompletedSubscription } from '../config/appsync/API';
import { Hub } from 'aws-amplify';
import { AttendeeUrlProcessingPolling } from '../lib/services/polling/attendee-url-processing-polling';
import { createSubscriptionFallback } from '../lib/services/polling/subscription-fallback';
import { AttendeeProcessingResultType, AttendeeProcessingUrlResponse } from '../lib/api';
import { resolveMeetingLinkJoinError, resolveMeetingLinkJoinValue } from '../lib/meeting-link-registration-result';
import { MEETING_REGISTRATION_FALLBACK_DELAY, MEETING_REGISTRATION_POLLING_WINDOW, THIRTY_SECONDS_TIMEOUT } from '../lib/constants';

type SubscriptionData = { value: { data: OnRegisterToMeetingCompletedSubscription } };

export type MeetingRegistrationSubscriptionState = {
  inProgress: boolean,
  joinUrl: null | string | undefined,
  uniqueDialInCode: null | string | undefined,
  error: null | string
}

export enum ACTION_TYPE {
  INITIALIZED = 'INITIALIZED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

type ACTION =
  | { type: ACTION_TYPE.INITIALIZED }
  | { type: ACTION_TYPE.COMPLETED; payload: TCompletedData }
  | { type: ACTION_TYPE.FAILED, payload?: string }
  ;

export type RegistrationResponse = { registrationId: string, appSyncToken?: string };

export type SubscriptionResult = {
  state: MeetingRegistrationSubscriptionState,
  subscribe(
    response: RegistrationResponse | void,
    isJoinMethodAudioBridge: boolean | void,
    fetchProcessingResult?: () => Promise<AttendeeProcessingUrlResponse>,
    joinMethodId?: number,
  ): Promise<void>,
  unsubscribe(): void
}

const _defaults: MeetingRegistrationSubscriptionState = {
  inProgress: false,
  error: null,
  joinUrl: null,
  uniqueDialInCode: null
};

type TJoinUrl = {
  joinUrl: string | null | undefined;
};

type TJuniqueDialInCode = {
  uniqueDialInCode: string | null | undefined;
};

type TCompletedData = TJoinUrl | TJuniqueDialInCode;

export function reducer(state: MeetingRegistrationSubscriptionState, action: ACTION): MeetingRegistrationSubscriptionState {
  switch (action.type) {
  case ACTION_TYPE.INITIALIZED:
    return { ..._defaults, inProgress: true };
  case ACTION_TYPE.COMPLETED:
    return { ..._defaults, ...action.payload };
  case ACTION_TYPE.FAILED:
    return { ..._defaults, error: action.payload || null };
  default:
    return _defaults;
  }
}


const initialState = { inProgress: false, joinUrl: null, error: null, uniqueDialInCode: null };

export const useMeetingRegistrationSubscription = (onSubscriptionStart: () => void): SubscriptionResult => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [cancelled, cancel] = useState<boolean>(false);
  const activeClient = useRef<{ unsubscribe(): void, closed: boolean } | null>(null);
  const activeFallback = useRef<{ stop(): void } | null>(null);
  const hubListenerRemover = useRef<(() => void) | null>(null);
  const timeoutIdRef = useRef<number | null>(null);
  const cancelSubscription = () => cancel(true);

  const clearSubscriptionTimeout = (): void => {
    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  const unsubscribe = (): void => {
    clearSubscriptionTimeout();
    activeFallback.current?.stop();
    activeFallback.current = null;
    if (activeClient.current && !activeClient.current.closed) {
      activeClient.current.unsubscribe();
    }
    activeClient.current = null;
    if (typeof hubListenerRemover.current === 'function') {
      hubListenerRemover.current();
      hubListenerRemover.current = null;
    }
  };

  const initSubscription = (client: { unsubscribe(): void, closed: boolean }, onTimeout?: () => void) => {
    onSubscriptionStart();
    timeoutIdRef.current = window.setTimeout(() => {
      onTimeout?.();
      if (!client.closed) client.unsubscribe();
      subscriptionFailed();
      cancelSubscription();
    }, THIRTY_SECONDS_TIMEOUT);

    dispatch({ type: ACTION_TYPE.INITIALIZED });
  };
  const subscriptionCompleted = (payload: TCompletedData) => dispatch({ type: ACTION_TYPE.COMPLETED, payload });
  const subscriptionFailed = (payload?: string) => dispatch({ type: ACTION_TYPE.FAILED, payload: payload || 'Processing failed' });

  return {
    state,
    async subscribe(
      response: RegistrationResponse,
      isJoinMethodAudioBridge: boolean,
      fetchProcessingResult?: () => Promise<AttendeeProcessingUrlResponse>,
      joinMethodId?: number,
    ): Promise<void> {
      return new Promise((resolve, reject) => {
        const toCompletedData = (value: string): TCompletedData => {
          return isJoinMethodAudioBridge
            ? { uniqueDialInCode: value }
            : { joinUrl: value };
        };

        const applyPollingResult = (pollingResponse: AttendeeProcessingUrlResponse): void => {
          if (pollingResponse.type === AttendeeProcessingResultType.NOT_AN_ATTENDEE) {
            subscriptionFailed(AttendeeProcessingResultType.NOT_AN_ATTENDEE);
          } else if (fetchProcessingResult) {
            const joinError = resolveMeetingLinkJoinError(pollingResponse, joinMethodId);
            if (joinError) {
              subscriptionFailed(joinError);
            } else {
              const resolvedValue = resolveMeetingLinkJoinValue(pollingResponse, joinMethodId);
              if (!resolvedValue) {
                return;
              }
              subscriptionCompleted(toCompletedData(resolvedValue));
            }
          } else if (pollingResponse.registrationResult) {
            subscriptionCompleted(toCompletedData(pollingResponse.registrationResult));
          } else {
            return;
          }
          clearSubscriptionTimeout();
        };

        if (!response?.registrationId) {
          resolve();
          return;
        }

        if (!response?.appSyncToken) {
          if (!fetchProcessingResult) {
            resolve();
            return;
          }

          unsubscribe();
          cancel(false);
          dispatch({ type: ACTION_TYPE.INITIALIZED });

          const polling = new AttendeeUrlProcessingPolling(
            response.registrationId,
            '',
            {
              pollingCancellationTimeout: MEETING_REGISTRATION_POLLING_WINDOW,
              fetchResult: fetchProcessingResult
            }
          );
          activeFallback.current = { stop: () => polling.stop() };
          polling.start()
            .then(applyPollingResult)
            .catch(() => {
              subscriptionFailed();
              cancelSubscription();
              reject(new Error('ConnectionDisrupted'));
            });
          polling.subscribeToPollingStart(resolve);
          return;
        }

        const params = [response.registrationId, response.appSyncToken] as [string, string];

        unsubscribe();
        cancel(false);

        const pollingSettings = {
          pollingCancellationTimeout: MEETING_REGISTRATION_POLLING_WINDOW,
          ...(fetchProcessingResult ? { fetchResult: fetchProcessingResult } : {})
        };
        const disruptedPollingSettings = {
          pollingCancellationTimeout: THIRTY_SECONDS_TIMEOUT,
          ...(fetchProcessingResult ? { fetchResult: fetchProcessingResult } : {})
        };

        // eslint-disable-next-line prefer-const
        let client: { unsubscribe(): void, closed: boolean };
        let pollingFallbackStarted = false;
        let processingResultNotified = false;

        const notifyProcessingResultOnce = (): void => {
          if (!fetchProcessingResult || processingResultNotified || cancelled) {
            return;
          }
          processingResultNotified = true;
          void fetchProcessingResult().catch(() => undefined);
        };

        const fallback = createSubscriptionFallback({
          startDelay: MEETING_REGISTRATION_FALLBACK_DELAY,
          isClientClosed: () => !client || client.closed,
          createPolling: () => new AttendeeUrlProcessingPolling(
            response?.registrationId,
            response?.appSyncToken,
            pollingSettings
          ),
          onPollingResult: (pollingResponse) => {
            applyPollingResult(pollingResponse);
            if (client && !client.closed) client.unsubscribe();
          }
        });

        const cancelSubscriptionOnConnectionFailure = (): void => {
          fallback.stop();
          if (client && !client.closed) client.unsubscribe();
          clearSubscriptionTimeout();
          subscriptionFailed();
          cancelSubscription();
        };

        const startPollingFallback = (): void => {
          if (pollingFallbackStarted) {
            return;
          }
          pollingFallbackStarted = true;

          fallback.stop();
          if (client && !client.closed) client.unsubscribe();
          clearSubscriptionTimeout();

          const polling = new AttendeeUrlProcessingPolling(
            response?.registrationId,
            response?.appSyncToken,
            disruptedPollingSettings
          );
          activeFallback.current = { stop: () => polling.stop() };

          polling.start()
            .then(applyPollingResult)
            .catch(() => {
              cancelSubscriptionOnConnectionFailure();
              reject(new Error('ConnectionDisrupted'));
            });

          polling.subscribeToPollingStart(resolve);
        };

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore*/
        client = (registerMeetingCompleteSubscription(...params) as Observable<SubscriptionData>)
          .subscribe({
            next({ value }: SubscriptionData): void {
              fallback.stop();
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore*/
              const error = value?.data?.onRegisterToMeetingCompleted?.error;
              const registrationResult = value?.data?.onRegisterToMeetingCompleted?.registrationResult ?? '';
              const joinError = fetchProcessingResult
                ? resolveMeetingLinkJoinError({ registrationResult }, joinMethodId)
                : null;
              const resolvedValue = fetchProcessingResult
                ? resolveMeetingLinkJoinValue({ registrationResult }, joinMethodId)
                : registrationResult;

              if (!cancelled && !error && joinError) {
                notifyProcessingResultOnce();
                subscriptionFailed(joinError);
                clearSubscriptionTimeout();
                client.unsubscribe();
                return;
              }

              if (!cancelled && !error && fetchProcessingResult && !resolvedValue) {
                startPollingFallback();
                return;
              }

              if (!cancelled && !error) {
                notifyProcessingResultOnce();
                subscriptionCompleted(toCompletedData(resolvedValue || ''));
                clearSubscriptionTimeout();
              }

              if (!cancelled && error) {
                notifyProcessingResultOnce();
                subscriptionFailed(error);
                clearSubscriptionTimeout();
              }

              client.unsubscribe();
            },
            error(): void {
              if (fetchProcessingResult) {
                startPollingFallback();
                return;
              }
              fallback.stop();
              if (client && !client.closed) client.unsubscribe();
              clearSubscriptionTimeout();
              subscriptionFailed();
              cancelSubscription();
              reject(new Error('ConnectionDisrupted'));
            },
            complete(): void {
              fallback.stop();
              clearSubscriptionTimeout();
              if (client && !client.closed) client.unsubscribe();
            }
          })
        ;

        activeClient.current = client;
        if (pollingFallbackStarted) {
          if (client && !client.closed) client.unsubscribe();
          dispatch({ type: ACTION_TYPE.INITIALIZED });
        } else {
          activeFallback.current = fallback;
          initSubscription(client, fallback.stop);
        }

        if (!pollingFallbackStarted) {
          /* Resolve when the subscription connection is established or reject if client fails to connect */
          hubListenerRemover.current = Hub.listen('api', ({ payload }) => {
            const { event, data } = payload;

            if (event === 'Subscription ack') {
              fallback.start();
              resolve();
              return;
            }

            if (data?.connectionState === 'ConnectionDisrupted') {
              startPollingFallback();
            }
          });
        }
      });

    },
    unsubscribe
  };
};
